/**
 * Client for the self-service data-rights control-plane routes (#41 Phase C, FR-14.2/14.3):
 *
 *   - {@link exportAccountData} — `GET /api/account/export`, the caller's personal data as
 *     one JSON artifact ({@link AccountExport}). Identity is the signed guest token in the
 *     `x-guest-token` header (a GET with no body, like `fetchCreditBalance`).
 *   - {@link deleteAccount} — `POST /api/account/delete`, the self-service erasure cascade
 *     (Phase B endpoint; the UI that calls it ships in Phase C). Identity is the signed
 *     guest token in the JSON body.
 *   - {@link downloadJson} — trigger a browser "Save as…" for the exported artifact so the
 *     token never rides a navigational URL (the export is fetched, then saved as a Blob).
 *
 * The server enforces registered-only (`403 guest_forbidden`, D-41.5) and reads the
 * identity from the signed token, never a client-supplied id (FR-9.3). Response shapes come
 * from `@jeopardy/shared` so client and server can't drift (AGENT_GUIDE rule 2).
 */

import type { AccountExport } from '@jeopardy/shared';
import { SERVER_HTTP_URL } from '../config';
import { ApiError } from './api-error';

/**
 * Fetch the caller's full data export. Throws an {@link ApiError} on any non-2xx (a guest
 * is `403`, an unauthenticated call `401`), which the caller maps to friendly copy.
 */
export async function exportAccountData(guestToken: string): Promise<AccountExport> {
  const res = await fetch(`${SERVER_HTTP_URL}/api/account/export`, {
    headers: { 'x-guest-token': guestToken },
  });
  if (!res.ok) {
    throw new ApiError(`Could not export your data (${res.status})`, res.status);
  }
  return (await res.json()) as AccountExport;
}

/**
 * Permanently delete the caller's account + personal data (FR-14.2). Idempotent
 * server-side. Throws an {@link ApiError} on any non-2xx so the caller can surface it
 * without navigating away.
 */
export async function deleteAccount(guestToken: string): Promise<void> {
  const res = await fetch(`${SERVER_HTTP_URL}/api/account/delete`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ guestToken }),
  });
  if (!res.ok) {
    throw new ApiError(`Could not delete your account (${res.status})`, res.status);
  }
}

/**
 * Trigger a browser download of `data` as a pretty-printed JSON file named `filename`.
 * Uses an object URL + a transient anchor click; revokes the URL afterward. Kept here so
 * both the component and its test share one code path.
 */
export function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
